/**
 * `/api/threads` — person-to-person threads (gh #4661). A thread lives in its ThreadDO; each
 * member's thread list is an index of it. Today a thread is the personal 1:1 of a contact
 * pair; groups and org-linked threads use the same shapes, so every field they need is
 * already here (`kind`, `org_id`, `org_name`, a null preview, the content-free frame).
 *
 * Messages are ordered by `seq`, the thread's own counter: pages, read cursors and unread
 * counts all use it, never a timestamp. A member sees only the messages visible to them: a
 * message a blocked person sends is stored visible only to its author (Decision 7), so two
 * members of one thread can see different last messages and counts.
 *
 * Names are display names only, never emails (Decision 11). A null `display_name` renders as
 * "WayAI user"; `deleted_user: true` renders as "Deleted user" (Decision 6).
 *
 * Identifiers carry a `people` prefix: "thread" already names the hub conversation view.
 */

import { z } from 'zod';
import { timestampSchema, uuidSchema } from './common';
import { peopleUserIdSchema, type PeopleIncomingInvite, type PeoplePage } from './people-contacts';

// =============================================================================
// LIMITS (#4661 Caps, Limits). Code constants: raising one is a code change.
// =============================================================================

/** The longest message body, in characters. */
export const PEOPLE_THREAD_MESSAGE_MAX_LENGTH = 4_000;
/** Messages per page (≤ 50 × 4,000 characters per RPC: the 32 MiB rule). */
export const PEOPLE_THREAD_MESSAGES_PAGE_MAX = 50;
/** Rows per page of a member's thread list. */
export const PEOPLE_THREAD_PAGE_MAX = 100;
export const PEOPLE_THREAD_PAGE_DEFAULT = 50;
/** Thread-list rows the session snapshot carries; the rest page through `GET /api/threads`. */
export const PEOPLE_THREAD_HEAD_MAX = 20;
/** The longest preview of a thread's last message a list row carries, in characters. */
export const PEOPLE_THREAD_PREVIEW_MAX_LENGTH = 200;

// =============================================================================
// REQUEST SCHEMAS
// =============================================================================

/** Every `/api/threads/:threadId…` route. */
export const peopleThreadIdParam = z.object({ threadId: uuidSchema });

/** DELETE /api/threads/:threadId/messages/:messageId */
export const peopleThreadMessageIdParam = z.object({ threadId: uuidSchema, messageId: uuidSchema });

/** GET /api/threads — an opaque cursor and a page size. */
export const peopleThreadListQuery = z.object({
  cursor: z.string().min(1).max(256).optional(),
  limit: z.coerce.number().int().min(1).max(PEOPLE_THREAD_PAGE_MAX).default(PEOPLE_THREAD_PAGE_DEFAULT),
});

/** GET /api/threads/:threadId/messages — the page of messages before `before_seq`. */
export const peopleThreadMessagesQuery = z.object({
  before_seq: z.coerce.number().int().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(PEOPLE_THREAD_MESSAGES_PAGE_MAX).default(PEOPLE_THREAD_MESSAGES_PAGE_MAX),
});

/**
 * POST /api/threads/:threadId/messages. `client_message_id` makes a retried send idempotent:
 * a second send with the same id from the same author answers the first message.
 */
export const peopleThreadSendBody = z
  .object({
    body: z
      .string()
      .max(PEOPLE_THREAD_MESSAGE_MAX_LENGTH)
      .refine((s) => s.trim().length > 0, 'must not be empty'),
    client_message_id: uuidSchema.optional(),
  })
  .strict();

/** POST /api/threads/:threadId/read — everything up to `seq` has been read. */
export const peopleThreadReadBody = z.object({ seq: z.number().int().min(0) }).strict();

/**
 * PATCH /api/threads/:threadId — this member's own settings. `muted`: no push, unread still
 * counts. `hidden`: out of the list; only a read-only thread can be hidden, and a new message
 * brings it back.
 */
export const peopleThreadSettingsBody = z
  .object({
    muted: z.boolean().optional(),
    hidden: z.boolean().optional(),
  })
  .strict()
  .refine((b) => b.muted !== undefined || b.hidden !== undefined, 'nothing to change');

export type PeopleThreadListQuery = z.infer<typeof peopleThreadListQuery>;
export type PeopleThreadMessagesQuery = z.infer<typeof peopleThreadMessagesQuery>;
export type PeopleThreadSendInput = z.infer<typeof peopleThreadSendBody>;
export type PeopleThreadSettingsInput = z.infer<typeof peopleThreadSettingsBody>;

// =============================================================================
// RESPONSE SCHEMAS
// =============================================================================

/** `dm` is a 1:1; `group` is a named group (a later PR of #4661). */
export const peopleThreadKindSchema = z.enum(['dm', 'group']);

/** A member as other members see them: a display name, never an email. */
export const peopleThreadMemberSchema = z
  .object({
    user_id: peopleUserIdSchema,
    display_name: z.string().nullable(),
    deleted_user: z.boolean(),
  })
  .strict();

/**
 * A thread's latest message as a list row shows it. `preview` is null for an org-linked
 * thread (its rows carry no text, Decision 11) and for a deleted message.
 */
export const peopleThreadLastMessageSchema = z
  .object({
    message_id: uuidSchema,
    seq: z.number().int().positive(),
    author_user_id: peopleUserIdSchema,
    preview: z.string().nullable(),
    deleted: z.boolean(),
    created_at: timestampSchema,
  })
  .strict();

/**
 * One row of a member's thread list, as that member sees the thread.
 *
 * - `title`: a group's name, or a 1:1's counterpart's display name; null renders from
 *   `counterpart` ("WayAI user", or "Deleted user").
 * - `counterpart`: the other member of a 1:1; null for a group.
 * - `org_id` / `org_name`: the owning org of an org-linked thread; null for a personal one.
 * - `last_seq`: the `seq` of the last message this member can see (0 for none).
 * - `unread_count`: messages from others after `read_seq`, not deleted.
 * - `writable`: whether this member may send. A personal 1:1 is writable while the pair are
 *   contacts and this member does not block the other (Decision 3).
 */
export const peopleThreadSummarySchema = z
  .object({
    thread_id: uuidSchema,
    kind: peopleThreadKindSchema,
    org_id: z.string().nullable(),
    org_name: z.string().nullable(),
    title: z.string().nullable(),
    counterpart: peopleThreadMemberSchema.nullable(),
    last_seq: z.number().int().nonnegative(),
    last_message: peopleThreadLastMessageSchema.nullable(),
    last_activity_at: timestampSchema,
    read_seq: z.number().int().nonnegative(),
    unread_count: z.number().int().nonnegative(),
    muted: z.boolean(),
    hidden: z.boolean(),
    writable: z.boolean(),
  })
  .strict();

/**
 * One message. A deleted message stays as a tombstone: `body` null, `deleted` true (Decision
 * 6). `client_message_id` is the author's own idempotency key, shown only to the author.
 */
export const peopleThreadMessageSchema = z
  .object({
    message_id: uuidSchema,
    seq: z.number().int().positive(),
    author_user_id: peopleUserIdSchema,
    body: z.string().nullable(),
    deleted: z.boolean(),
    created_at: timestampSchema,
    deleted_at: timestampSchema.nullable(),
    client_message_id: uuidSchema.nullable(),
  })
  .strict();

export type PeopleThreadKind = z.infer<typeof peopleThreadKindSchema>;
export type PeopleThreadMember = z.infer<typeof peopleThreadMemberSchema>;
export type PeopleThreadLastMessage = z.infer<typeof peopleThreadLastMessageSchema>;
export type PeopleThreadSummary = z.infer<typeof peopleThreadSummarySchema>;
export type PeopleThreadMessage = z.infer<typeof peopleThreadMessageSchema>;

/**
 * A page of messages, oldest first. `members` names every author on the page.
 * `next_before_seq` continues toward older messages; null when there are none.
 */
export interface PeopleThreadMessagesPage {
  messages: PeopleThreadMessage[];
  members: PeopleThreadMember[];
  next_before_seq: number | null;
}

/** A thread as its member opens it: the list row and the newest page of messages. */
export interface PeopleThreadView extends PeopleThreadMessagesPage {
  thread: PeopleThreadSummary;
}

/** GET /api/threads — newest activity first; hidden threads are left out. */
export type PeopleThreadsResponse = PeoplePage<PeopleThreadSummary>;
/** GET /api/threads/:threadId */
export interface PeopleThreadViewResponse {
  data: PeopleThreadView;
}
/** GET /api/threads/:threadId/messages */
export interface PeopleThreadMessagesResponse {
  data: PeopleThreadMessagesPage;
}
/** POST /api/threads/:threadId/messages, DELETE …/messages/:messageId */
export interface PeopleThreadMessageResponse {
  data: { message: PeopleThreadMessage; thread: PeopleThreadSummary };
}
/** POST /api/threads/:threadId/read, PATCH /api/threads/:threadId */
export interface PeopleThreadSummaryResponse {
  data: PeopleThreadSummary;
}

// =============================================================================
// LIVE FRAMES (through the member's LiveUpdateSession)
//
// A frame can repeat a message an earlier one carried, with the message as it stands when the
// frame is built: one deleted meanwhile arrives as its tombstone. So a client keys messages by
// `message_id`, orders them by `seq`, and keeps a deletion once it has seen one. A member whose
// deliveries keep failing can miss message frames past the newest 50 owed to them; the thread
// row's `last_seq` (in any later frame, the list or the snapshot) says which to fetch. A
// delete-for-everyone frame is kept owed far longer (up to 1,000), since nothing else tells a
// client that a message it holds was deleted. A row that exhausts its retries is dropped with
// every frame it held; the member's next open of the thread repairs their row, and refetching
// the pages a client shows brings its messages up to date.
// =============================================================================

/** The live frame types of this surface. */
export const PEOPLE_LIVE_FRAME_TYPES = [
  'thread_message',
  'thread_updated',
  'thread_message_deleted',
  'contact_invite',
] as const;
export type PeopleLiveFrameType = (typeof PEOPLE_LIVE_FRAME_TYPES)[number];

/**
 * `thread_message` — a message this member can see. An org-linked thread's frame is
 * content-free (`message: null`): the client fetches the message through the role-checked
 * `GET /api/threads/:threadId`.
 */
export type PeopleThreadMessageFrame =
  | { thread: PeopleThreadSummary; content_free: false; message: PeopleThreadMessage }
  | { thread: PeopleThreadSummary; content_free: true; message: null };

/** `thread_updated` — this member's list row changed (read, mute, hide, title, writability). */
export interface PeopleThreadUpdatedFrame {
  thread: PeopleThreadSummary;
}

/** `thread_message_deleted` — a message this member could see was deleted for everyone. */
export interface PeopleThreadMessageDeletedFrame {
  thread: PeopleThreadSummary;
  message_id: string;
  seq: number;
}

/**
 * `contact_invite` — this member's pending incoming invites changed. `invite` is the one that
 * arrived, or null when one left (accepted, declined, cancelled or expired).
 */
export interface PeopleContactInviteFrame {
  pending_invite_count: number;
  invite: PeopleIncomingInvite | null;
}

/** A people frame: its type and the payload that type carries. */
export type PeopleLiveFrame =
  | { type: 'thread_message'; payload: PeopleThreadMessageFrame }
  | { type: 'thread_updated'; payload: PeopleThreadUpdatedFrame }
  | { type: 'thread_message_deleted'; payload: PeopleThreadMessageDeletedFrame }
  | { type: 'contact_invite'; payload: PeopleContactInviteFrame };

// =============================================================================
// SESSION SNAPSHOT (`SessionSnapshot.people`)
// =============================================================================

/**
 * The people surface's first render, carried by the session snapshot while the surface is
 * on and only for an interactive sign-in session.
 */
export interface PeopleSessionSnapshot {
  /** The newest `PEOPLE_THREAD_HEAD_MAX` rows of the thread list. */
  threads: PeopleThreadSummary[];
  /** Continues the list through `GET /api/threads?cursor=`; null when the head is all of it. */
  next_cursor: string | null;
  /** Pending incoming contact invites. */
  pending_invite_count: number;
  /** The thread the bootstrap's `active_thread_id` hint names, when the viewer is a member. */
  active_thread: PeopleThreadView | null;
}
